import { useState } from 'react'
import { TRowPreview } from './TRowPreview'
import { UnitDetailPanel } from './UnitDetailPanel'
import { Button } from '@/components/ui/button'
import { Plus, RefreshCw } from 'lucide-react'
import type { HangarUnitRow, HangarUnitUpdate, GeneratedUnit } from '@/types/hangar-units'
import type { TRowTypeConfig, PricingModel } from '@/types/hangars'

interface TRowEditorProps {
  /** Existing units to display */
  units: HangarUnitRow[]
  /** Type config with template dimensions */
  typeConfig: TRowTypeConfig
  /** Parent hangar's pricing model */
  parentPricingModel: PricingModel
  /** Parent hangar's monthly rate (for fixed pricing) */
  parentMonthlyRate?: number | null
  /** Parent hangar's rate per sqft (for per_sqft pricing) */
  parentRatePerSqft?: number | null
  /** Called when a unit is updated */
  onUpdateUnit: (id: string, data: HangarUnitUpdate) => void
  /** Called when a unit is deleted */
  onDeleteUnit: (id: string) => void
  /** Called when user wants to regenerate all units */
  onRegenerate?: () => void
  /** Called when user wants to add a new unit */
  onAddUnit?: () => void
  /** Whether mutations are in progress */
  isLoading?: boolean
}

/**
 * Combined editor for T-hangar units
 * Shows SVG preview with clickable units and detail panel for selected unit
 */
export function TRowEditor({
  units,
  typeConfig,
  parentPricingModel,
  parentMonthlyRate,
  parentRatePerSqft,
  onUpdateUnit,
  onDeleteUnit,
  onRegenerate,
  onAddUnit,
  isLoading,
}: TRowEditorProps) {
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null)

  const selectedUnit = units.find((u) => u.id === selectedUnitId)

  const handleUnitClick = (unit: HangarUnitRow | GeneratedUnit) => {
    if ('id' in unit) {
      setSelectedUnitId(unit.id)
    }
  }

  const handleClosePanel = () => {
    setSelectedUnitId(null)
  }

  const handleSaveUnit = (id: string, data: HangarUnitUpdate) => {
    onUpdateUnit(id, data)
  }

  const handleDeleteUnit = (id: string) => {
    onDeleteUnit(id)
    setSelectedUnitId(null)
  }

  // Extract template from type config
  const template = typeConfig.unit_template ?? {
    door_width: 42,
    depth: 32,
    tail_width: 24,
  }

  const taxilaneWidth = typeConfig.shared_taxilane_width ?? 50

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {units.length} unit{units.length !== 1 ? 's' : ''} configured
          {selectedUnit && (
            <span className="ml-2 text-foreground">
              — Selected: <strong>{selectedUnit.unit_code}</strong>
            </span>
          )}
        </div>
        <div className="flex gap-2">
          {onAddUnit && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onAddUnit}
              disabled={isLoading}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Unit
            </Button>
          )}
          {onRegenerate && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRegenerate}
              disabled={isLoading}
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Regenerate
            </Button>
          )}
        </div>
      </div>

      {/* SVG Preview */}
      <div className="border rounded-md p-4 bg-white">
        <TRowPreview
          units={units}
          template={template}
          taxilaneWidth={taxilaneWidth}
          selectedUnitId={selectedUnitId ?? undefined}
          onUnitClick={handleUnitClick}
          interactive={true}
        />
      </div>

      {/* Selected Unit Detail Panel */}
      {selectedUnit && (
        <UnitDetailPanel
          unit={selectedUnit}
          template={template}
          parentPricingModel={parentPricingModel}
          parentMonthlyRate={parentMonthlyRate}
          parentRatePerSqft={parentRatePerSqft}
          onSave={handleSaveUnit}
          onDelete={handleDeleteUnit}
          onClose={handleClosePanel}
          isLoading={isLoading}
        />
      )}

      {/* Help text when no unit selected */}
      {!selectedUnit && units.length > 0 && (
        <p className="text-sm text-muted-foreground text-center py-2">
          Click a unit in the preview above to edit its properties
        </p>
      )}
    </div>
  )
}
